import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

const visuallyHiddenSx = visuallyHidden as SxProps<Theme>;

export function PublicSignerOverlayButton({
  ariaLabel,
  ariaChecked,
  children,
  errorMessage,
  errorId: errorIdOverride,
  fieldId,
  hasError = false,
  renderErrorMessage = true,
  role,
  sx,
  onClick
}: {
  ariaLabel: string;
  ariaChecked?: boolean;
  children: ReactNode;
  errorMessage?: string;
  errorId?: string;
  fieldId: string;
  hasError?: boolean;
  onClick: () => void;
  renderErrorMessage?: boolean;
  role?: 'checkbox' | 'radio';
  sx?: SxProps<Theme>;
}) {
  const errorId =
    errorIdOverride ??
    (hasError && errorMessage
      ? `public-signer-field-error-${fieldId}`
      : undefined);

  return (
    <>
      <Box
        aria-checked={ariaChecked}
        aria-describedby={errorId}
        aria-invalid={hasError || undefined}
        aria-label={ariaLabel}
        component="button"
        data-public-signer-field-id={fieldId}
        onClick={onClick}
        role={role}
        sx={
          [
            (theme) => ({
              alignItems: 'center',
              appearance: 'none',
              backgroundColor: 'transparent',
              border: 0,
              color: 'inherit',
              cursor: 'pointer',
              display: 'flex',
              height: '100%',
              justifyContent: 'center',
              p: 0,
              borderRadius: 'inherit',
              textAlign: 'center',
              transition:
                'background-color 120ms ease, box-shadow 120ms ease, outline-color 120ms ease',
              width: '100%',
              '&:hover': {
                backgroundColor: hasError
                  ? alpha(theme.palette.error.main, 0.08)
                  : alpha(theme.palette.secondary.main, 0.04)
              },
              '&:active': {
                backgroundColor: hasError
                  ? alpha(theme.palette.error.main, 0.12)
                  : alpha(theme.palette.secondary.main, 0.08)
              },
              '&:focus-visible': {
                backgroundColor: hasError
                  ? alpha(theme.palette.error.main, 0.08)
                  : alpha(theme.palette.secondary.main, 0.04),
                boxShadow: hasError
                  ? `inset 0 0 0 2px ${theme.palette.error.main}`
                  : `inset 0 0 0 2px ${theme.palette.secondary.main}`,
                outline: 'none'
              }
            }),
            ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
          ] as SxProps<Theme>
        }
        type="button"
      >
        {children}
      </Box>
      {renderErrorMessage && errorId && errorMessage ? (
        <Box component="span" id={errorId} sx={visuallyHiddenSx}>
          {errorMessage}
        </Box>
      ) : null}
    </>
  );
}
